/**
 * 1 つの KUMODeck アカウント（2026-10-07 console/one-account）の、API とブラウザ（storage・location）に触る部分。
 * 判断は oneAccount.ts（node --test）。ここは api.ts とつなぐだけ。
 *
 * 決め事（oneAccount.test.ts が文字として確かめる）: このファイルの api() は**必ず auth: false** で呼び、鍵は accountHeaders(kas_) で付ける。
 *   api() は auth を省くと作り手の鍵を自動で付ける = Player の口に作り手の鍵を流さないため（設計 §3 の不変条件）。
 */
import { api } from './api';
import { challengeFor, randomToken } from './devOAuth';
import { accountHeaders, consoleSignInUrl, readConsoleSession, savePendingSignIn, type ConsoleSession } from './oneAccount';

// ---------------------------------------------------------------- kas_ の置き場

const ACCOUNT_TOKEN_KEY = 'kumo.console.accountToken';

/** kas_（Player の口の鍵）。作り手の鍵と同じく localStorage（API 全体の方針 = Cookie を使わない・api.ts の冒頭） */
export function getAccountToken(): string | null {
  try {
    return localStorage.getItem(ACCOUNT_TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setAccountToken(token: string | null) {
  try {
    if (token) localStorage.setItem(ACCOUNT_TOKEN_KEY, token);
    else localStorage.removeItem(ACCOUNT_TOKEN_KEY);
  } catch {
    /* プライベートモード等。セッションはメモリ上だけになる */
  }
}

const sessionStore = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};
export const signInPendingStorage = sessionStore;

// ---------------------------------------------------------------- ログインの往復

/**
 * account のログインへ移る。verifier をこのタブの sessionStorage に置いてから移る（置けなければ false = 呼んだ画面が案内を出す）。
 * returnTo = 入った後に戻る画面（/cli/device?code=… など。App.tsx の catch-all が state.from に入れたもの）
 */
export async function startAccountSignIn(accountUrl: string, returnTo: string): Promise<boolean> {
  // PKCE は開発者の OAuth と同じ作り方（devOAuth.ts: 32 バイト = 43 文字・challenge = base64url(sha256)）
  const verifier = randomToken();
  const challenge = await challengeFor(verifier);
  if (!savePendingSignIn(sessionStore(), { verifier, returnTo, createdAt: Date.now() })) return false;
  window.location.assign(consoleSignInUrl(accountUrl, challenge));
  return true;
}

/** POST /v1/account/console/session（認証なし。コードと verifier だけ） */
export async function createConsoleSession(code: string, verifier: string): Promise<ConsoleSession> {
  const r = await api<unknown>('/v1/account/console/session', { method: 'POST', body: { code, verifier }, auth: false });
  const s = readConsoleSession(r);
  if (!s) throw new Error('Unexpected sign-in response');
  return s;
}

// ---------------------------------------------------------------- Player の口（kas_ だけ）

export type AccountMethod = 'email' | 'google' | 'x' | 'discord' | 'apple' | 'github';
export interface AccountGame {
  environmentId: string;
  gameName: string;
  testVersion: boolean;
  connectedAt: string;
  lastSignInAt: string | null;
  /** Kumo の配信で公開中のゲームの URL（2026-10-08 console/account-pages・サーバーの playLinks.ts）。null = よそで配っている = Play を出さない */
  playUrl: string | null;
}
export interface AccountMe {
  account: { email: string | null; displayName: string | null };
  methods: AccountMethod[];
  addableMethods: AccountMethod[];
  games: AccountGame[];
  creator: boolean;
  /**
   * Delete account を出してよいか（2026-10-08 core/one-account-3）。false = 作り手の中身が結ばれている（閉じた作り手も）= DELETE /v1/account は 409。
   * 無い（古いサーバー）ときは今までどおりボタンを出す（サーバーが 409 creator_account で断る）
   */
  deletable?: boolean;
}

export const getAccountMe = (token: string) => api<AccountMe>('/v1/account/me', { auth: false, headers: accountHeaders(token) });

/** 表示名（1〜32 字。空なら消す = サーバーは null / '' で消す） */
export const renameAccount = (token: string, displayName: string) =>
  api<{ account: AccountMe['account'] }>('/v1/account/me', { method: 'PATCH', body: { displayName: displayName || null }, auth: false, headers: accountHeaders(token) });

/** ゲームを外す（そのゲームの全端末のログインも切れる） */
export const disconnectGame = (token: string, environmentId: string) =>
  api<null>(`/v1/account/games/${encodeURIComponent(environmentId)}`, { method: 'DELETE', auth: false, headers: accountHeaders(token) });

/**
 * 一覧から隠す（Remove from this list・2026-10-08 console/account-pages）。外す（disconnectGame）と違ってサインアウトしない。
 * 次にそのゲームに入るとまた一覧に出る（サーバーの gameShelf.ts）
 */
export const hideGame = (token: string, environmentId: string) =>
  api<null>(`/v1/account/games/${encodeURIComponent(environmentId)}/hide`, { method: 'POST', auth: false, headers: accountHeaders(token) });

/** 入り方を外す（最後の 1 つは 409 last_method） */
export const removeAccountMethod = (token: string, method: AccountMethod) =>
  api<null>(`/v1/account/methods/${encodeURIComponent(method)}`, { method: 'DELETE', auth: false, headers: accountHeaders(token) });

/** この kas_ だけを失効させる（ほかの端末・account の Cookie はそのまま）。ダッシュボードのログアウトで呼ぶ（Core 7dc543c8） */
export const logoutAccount = (token: string) => api<null>('/v1/account/logout', { method: 'POST', body: {}, auth: false, headers: accountHeaders(token) });

/** アカウントの削除（作り手の中身がある人は 409 creator_account = 作り手の閉鎖は今の手順） */
export const deleteAccount = (token: string) => api<null>('/v1/account', { method: 'DELETE', auth: false, headers: accountHeaders(token) });

export interface StartedCreator {
  /** 作り手の鍵（この後は今までの作り手の画面がこれで読む = session.tsx の signIn へ） */
  developerToken: string;
  developer: { id: string; email: string; displayName: string | null; emailVerified: boolean; hasPassword: boolean };
  /**
   * true = 新しく作らずに、同じメールの今の作り手を自動でつないだ（2026-10-08 founder・サーバーの platform/oneAccount.ts autoJoinCreator）。
   * 両方のメールが確かめられていてぶつかる物が無いときだけ。招待コードは要らない（送っても使わない）。画面の動きは作り手を始めたときと同じ
   */
  connected?: boolean;
  /** 招待なしの本体（2026-10-29 core/open-signup-build）で、付けたコードが使えなかったが作り手はできた = { reason }。読み方は inviteCode.ts の inviteNotAppliedReason */
  inviteNotApplied?: unknown;
}
/** 作り手を始める（招待コード）→ 201 StartedCreator・自動でつないだときは 200 StartedCreator（connected: true） */
export const startCreating = (token: string, inviteCode: string | null) =>
  api<StartedCreator>('/v1/account/creator', { method: 'POST', body: inviteCode ? { inviteCode } : {}, auth: false, headers: accountHeaders(token) });
